import { type QueryObserverResult } from '@tanstack/react-query'
import { LoaderCircleIcon } from 'lucide-react'
import { createContext, useContext, useMemo } from 'react'
import { Outlet } from 'react-router'

import { Banner } from 'slab'
import { useGetQuotingProspectsId } from 'utils/web_api'
import { type QuotingProspect } from 'utils/web_api.schemas'

import { useEmployer } from '../EmployerProvider'

export const ProspectContext = createContext<{
  prospect: QuotingProspect | null
  refetch: ReturnType<typeof useGetQuotingProspectsId>['refetch']
}>({
  prospect: null,
  refetch: async () => ({ data: null, isLoading: false, isError: false, error: null } as QueryObserverResult<unknown, unknown>),
})

export function useProspect () {
  return useContext(ProspectContext)
}

export function ProspectProvider () {
  const { employer } = useEmployer()
  const { data, isLoading, error, refetch } = useGetQuotingProspectsId(employer?.quoting_prospect_id || '')

  const value = useMemo(() => ({ prospect: data || null, refetch }), [data, refetch])

  if (isLoading) {
    return (
      <div className='flex grow items-center justify-center'>
        <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='mt-20 w-full max-w-[45rem] mx-auto'>
        <Banner type='critical' title='Error loading quote'>
          {error instanceof Error && error.message}
        </Banner>
      </div>
    )
  }

  return (
    <ProspectContext.Provider value={value}>
      <Outlet />
    </ProspectContext.Provider>
  )
}
